import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
} from '@mui/material';
import {
  Cancel as AnularIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../../components/common';
import { useVoucherStore } from '../store/voucherStore';
import { EjercicioService } from '../../ejercicios/services/ejercicioService';
import type { Voucher } from '../types/voucher';
import { FUENTES_CONTABLES } from '../types/voucher';
import type { EjercicioContable, PeriodoContable } from '../../ejercicios/types/ejercicio';
import { PERIODO_LABELS } from '../../ejercicios/types/ejercicio';

const VouchersPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    vouchers,
    loading,
    error,
    fetchVouchers,
    anularVoucher,
    deleteVoucher,
    clearError,
  } = useVoucherStore();

  const [ejercicios, setEjercicios] = useState<EjercicioContable[]>([]);
  const [periodos, setPeriodos] = useState<PeriodoContable[]>([]);
  const [selectedEjercicio, setSelectedEjercicio] = useState<number>(0);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const [selectedFuente, setSelectedFuente] = useState<string>('');

  useEffect(() => {
    loadEjercicios();
  }, []);

  const loadEjercicios = async () => {
    try {
      const data = await EjercicioService.getAll();
      setEjercicios(data);
      if (data.length > 0) {
        const latest = data[0];
        setSelectedEjercicio(latest.idEjercicio);
      }
    } catch (err) {
      // handled
    }
  };

  useEffect(() => {
    if (selectedEjercicio) {
      loadPeriodos(selectedEjercicio);
    }
  }, [selectedEjercicio]);

  const loadPeriodos = async (idEjercicio: number) => {
    try {
      const data = await EjercicioService.getPeriodos(idEjercicio);
      setPeriodos(data);
      if (data.length > 0) {
        // Select current month or first open period
        const currentMonth = new Date().getMonth() + 1;
        const currentPeriodo = data.find(p => p.numeroPeriodo === currentMonth) || data[0];
        setSelectedPeriodo(currentPeriodo.idPeriodo);
      }
    } catch (err) {
      // handled
    }
  };

  useEffect(() => {
    if (selectedPeriodo) {
      fetchVouchers(selectedPeriodo, selectedFuente || undefined);
    }
  }, [selectedPeriodo, selectedFuente, fetchVouchers]);

  const columns: GridColDef[] = [
    { field: 'numeroRegistro', headerName: 'N° Registro', width: 180 },
    {
      field: 'codigoFuente',
      headerName: 'Fuente',
      width: 100,
      renderCell: (params) => {
        const fuente = FUENTES_CONTABLES.find(f => f.value === params.value);
        return fuente?.label || params.value;
      },
    },
    {
      field: 'fechaMovimiento',
      headerName: 'Fecha',
      width: 120,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : '',
    },
    { field: 'glosa', headerName: 'Glosa', flex: 1, minWidth: 200 },
    { field: 'monedaOrigen', headerName: 'Mon.', width: 70 },
    {
      field: 'totalDebeMN',
      headerName: 'Debe MN',
      width: 130,
      type: 'number',
      renderCell: (params) => params.value?.toFixed(2),
    },
    {
      field: 'totalHaberMN',
      headerName: 'Haber MN',
      width: 130,
      type: 'number',
      renderCell: (params) => params.value?.toFixed(2),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'ACTIVO' ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <GridEditButton
            onClick={() => navigate(`/contabilidad/vouchers/${params.row.idVoucher}`)}
            ariaLabel={`Editar voucher ${params.row.numeroRegistro}`}
          />
          {params.row.estado === 'ACTIVO' && (
            <>
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleAnular(params.row.idVoucher)}
                title="Anular"
              >
                <AnularIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row.idVoucher)}
                title="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ),
    },
  ];

  const handleAnular = async (id: number) => {
    if (window.confirm('¿Está seguro de anular este voucher?')) {
      try {
        await anularVoucher(id);
      } catch (err) {
        // handled
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este voucher?')) {
      try {
        await deleteVoucher(id);
      } catch (err) {
        // handled
      }
    }
  };

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Vouchers Contables"
        subtitle="Explorador de asientos contables"
        actions={
          <AddButton text="Nuevo Voucher" onClick={() => navigate('/contabilidad/vouchers/nuevo')} />
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Ejercicio</InputLabel>
          <Select
            value={selectedEjercicio}
            label="Ejercicio"
            onChange={(e) => setSelectedEjercicio(Number(e.target.value))}
          >
            {ejercicios.map(ej => (
              <MenuItem key={ej.idEjercicio} value={ej.idEjercicio}>{ej.anio}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Periodo</InputLabel>
          <Select
            value={selectedPeriodo}
            label="Periodo"
            onChange={(e) => setSelectedPeriodo(Number(e.target.value))}
          >
            {periodos.map(p => (
              <MenuItem key={p.idPeriodo} value={p.idPeriodo}>
                {String(p.numeroPeriodo).padStart(2, '0')} - {PERIODO_LABELS[p.numeroPeriodo] || `Periodo ${p.numeroPeriodo}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Fuente</InputLabel>
          <Select
            value={selectedFuente}
            label="Fuente"
            onChange={(e) => setSelectedFuente(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {FUENTES_CONTABLES.map(f => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <DataGrid
          columns={columns}
          rows={vouchers}
          getRowId={(row: Voucher) => row.idVoucher}
        />
      )}
    </ListLayoutPanel>
  );
};

export default VouchersPage;
